import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Header } from '../../components/layout/Header';
import { Button, PageHeader, StatusBadge } from '../../components/ui';
import { createEpic, fetchEpics } from '../../lib/api/epics';
import { fetchRepoCatalog } from '../../lib/api/forgeConfig';
import { EPIC_KIND_META, EPIC_KIND_ORDER, describeIssue } from '../../lib/epics/describeIssue';
import type {
  CreateEpicKind,
  CreateEpicRequest,
} from '../../../../shared/types/epics';

/**
 * /epics/new — create a fresh GitHub issue that Forge picks up via
 * webhook. Supports all three kinds:
 *
 *   epic       — labels `epic` + `agent-ready`; one repo
 *   bugfix     — label `agent:bug`; one repo; planning step skipped
 *   cross-repo — label `epic-cross-repo`; anchor repo + N involved repos
 *
 * UX-09: the form now speaks in outcomes, not GitHub labels — kind is a
 * "What should Forge build?" card picker, the repo defaults to a chip
 * (not a bare <select>) once one is chosen, and every GitHub mechanic
 * (target repo, labels, involved repos) is folded into a collapsed
 * "What gets posted to GitHub" preview built from the pure
 * `describeIssue` helper. The request payload, validation and
 * post-create polling are unchanged.
 *
 * After the issue is created, polls /epics every 2s until the new
 * issue number turns up as a META row, then navigates to its detail
 * page. The poll is capped at 60s so a stuck webhook doesn't trap the
 * operator; an "Open on GitHub" link is always visible during the wait.
 */

const POLL_MS = 2_000;
const POLL_CAP_MS = 60_000;

/** Per-user "last repo used" memory (UX-09). Best-effort — private mode etc. */
const LAST_REPO_STORAGE_KEY = 'forge.newEpic.lastRepo';

function readLastRepo(): string | null {
  try {
    return localStorage.getItem(LAST_REPO_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeLastRepo(repo: string): void {
  try {
    localStorage.setItem(LAST_REPO_STORAGE_KEY, repo);
  } catch {
    /* localStorage unavailable (private mode / SSR) — not worth surfacing */
  }
}

const BODY_TEMPLATE = '## Context\n…\n\n## Acceptance criteria\n- …\n- …';

export const NewEpicPage: React.FC = () => {
  const navigate = useNavigate();
  const qc = useQueryClient();

  // ── Form state ────────────────────────────────────────────────────────
  const [kind, setKind] = useState<CreateEpicKind>('epic');
  const [anchorRepo, setAnchorRepo] = useState<string>('');
  // Once a repo is auto-selected (single repo, or remembered from last time)
  // it renders as a chip; clicking "change" reveals the full picker.
  const [repoPickerOpen, setRepoPickerOpen] = useState(false);
  const [involvedRepos, setInvolvedRepos] = useState<string[]>([]);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [autoApprove, setAutoApprove] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Repos to populate the dropdowns from. The catalog endpoint already
  // filters to repos in the user's accessible projects on the backend.
  const reposQ = useQuery({
    queryKey: ['repos', 'catalog'],
    queryFn: fetchRepoCatalog,
  });

  const repoOptions = useMemo(
    () => (reposQ.data ?? []).map((r) => r.fullName).sort(),
    [reposQ.data],
  );

  // Once the catalog loads, preselect the remembered repo (if it's still
  // around) or fall back to the first one, same as before UX-09.
  useEffect(() => {
    if (anchorRepo || repoOptions.length === 0) return;
    const lastRepo = readLastRepo();
    setAnchorRepo(lastRepo && repoOptions.includes(lastRepo) ? lastRepo : repoOptions[0]);
  }, [repoOptions, anchorRepo]);

  // Remember whichever repo ends up selected, so the next visit starts there.
  useEffect(() => {
    if (anchorRepo) writeLastRepo(anchorRepo);
  }, [anchorRepo]);

  // Cross-repo: anchor is always part of involvedRepos.
  useEffect(() => {
    if (kind !== 'cross-repo') return;
    if (anchorRepo && !involvedRepos.includes(anchorRepo)) {
      setInvolvedRepos((prev) => [anchorRepo, ...prev]);
    }
  }, [kind, anchorRepo, involvedRepos]);

  // Cross-repo needs at least 2 repos to mean anything. Hide the card while
  // the catalog is still loading or genuinely empty; once there's exactly
  // one repo, show it disabled with an explanation (acceptance criteria).
  const crossRepoDisabledReason =
    repoOptions.length === 1 ? 'Needs at least two repos' : null;
  const showCrossRepoCard = repoOptions.length > 0 && (repoOptions.length >= 2 || crossRepoDisabledReason);

  const titleError = submitted && title.trim() === '' ? 'Title is required' : null;
  const bodyError = submitted && body.trim() === '' ? 'Description is required' : null;
  const involvedError =
    submitted && kind === 'cross-repo' && involvedRepos.length < 2
      ? 'Cross-repo epics need at least two repos in scope'
      : null;

  const create = useMutation({
    mutationFn: (req: CreateEpicRequest) => createEpic(req),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['epics-counts'] }),
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    if (title.trim() === '' || body.trim() === '') return;
    if (!anchorRepo) return;
    if (kind === 'cross-repo' && involvedRepos.length < 2) return;
    create.mutate({
      kind,
      repo: anchorRepo,
      title: title.trim(),
      body: body.trim(),
      ...(kind === 'cross-repo' ? { involvedRepos: Array.from(new Set([anchorRepo, ...involvedRepos])) } : {}),
      ...(kind !== 'bugfix' && autoApprove ? { autoApprove: true } : {}),
    });
  };

  const toggleInvolved = (repo: string) => {
    if (repo === anchorRepo) return; // anchor is required
    setInvolvedRepos((prev) =>
      prev.includes(repo) ? prev.filter((r) => r !== repo) : [...prev, repo],
    );
  };

  const useTemplate = () => {
    setBody((prev) => (prev.trim() === '' ? BODY_TEMPLATE : `${prev}\n\n${BODY_TEMPLATE}`));
  };

  // Chip mode is the default once a repo is picked: a single repo never
  // needs the full picker, and a multi-repo catalog only shows it once the
  // operator explicitly asks to change ("touched" it).
  const showRepoChip = anchorRepo !== '' && repoOptions.length > 0 && (repoOptions.length === 1 || !repoPickerOpen);
  const repoLabel = kind === 'cross-repo' ? 'Main repo' : 'Repo';

  return (
    <div data-testid="new-epic-page" className="min-h-screen bg-slate-50">
      <Header title="New epic" />
      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-8 space-y-5">
        <Link to="/" className="text-xs text-primary-500 hover:text-primary-600 hover:underline">
          ← Back to epics
        </Link>
        <PageHeader
          title="New epic"
          description="Posts a GitHub issue with the right Forge labels. The orchestrator picks it up within seconds and starts planning."
        />

        {create.data ? (
          <PostCreateWaiting
            issueNumber={create.data.issueNumber}
            issueUrl={create.data.issueUrl}
            expectedRepo={kind === 'cross-repo' ? null : anchorRepo}
          />
        ) : (
          <form onSubmit={onSubmit} className="space-y-5 bg-white border border-slate-200 rounded-xl shadow-card p-5">
            <fieldset className="space-y-2">
              <legend className="text-sm font-semibold text-slate-900">What should Forge build?</legend>
              <div
                role="radiogroup"
                aria-label="Epic kind"
                data-testid="new-epic-kind"
                className="grid grid-cols-1 sm:grid-cols-3 gap-2"
              >
                {EPIC_KIND_ORDER.filter((v) => v !== 'cross-repo' || showCrossRepoCard).map((v) => {
                  const meta = EPIC_KIND_META[v];
                  const active = kind === v;
                  const disabledReason = v === 'cross-repo' ? crossRepoDisabledReason : null;
                  return (
                    <button
                      key={v}
                      type="button"
                      role="radio"
                      aria-checked={active}
                      disabled={!!disabledReason}
                      title={disabledReason ?? undefined}
                      onClick={() => setKind(v)}
                      data-testid={`new-epic-kind-${v}`}
                      className={
                        'text-left rounded-lg border p-3 transition-colors ' +
                        (disabledReason
                          ? 'border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed'
                          : active
                          ? 'border-primary-500 bg-primary-50 text-primary-900 ring-1 ring-primary-500'
                          : 'border-slate-200 hover:border-slate-300 text-slate-700')
                      }
                    >
                      <div className="text-sm font-semibold">{meta.cardTitle}</div>
                      <div className={'text-xs mt-0.5 ' + (disabledReason ? '' : 'text-slate-500')}>
                        {meta.cardDescription}
                      </div>
                    </button>
                  );
                })}
              </div>
            </fieldset>

            <div>
              <label htmlFor="anchor-repo" className="block text-xs uppercase tracking-wide text-slate-500 font-semibold mb-1.5">
                {repoLabel}
              </label>
              {showRepoChip ? (
                <div className="flex items-center gap-2" data-testid="new-epic-repo-chip">
                  <StatusBadge tone="neutral">{anchorRepo}</StatusBadge>
                  {repoOptions.length > 1 && (
                    <button
                      type="button"
                      onClick={() => setRepoPickerOpen(true)}
                      data-testid="new-epic-repo-change"
                      className="text-xs text-primary-500 hover:text-primary-600 hover:underline"
                    >
                      change
                    </button>
                  )}
                </div>
              ) : (
                <select
                  id="anchor-repo"
                  value={anchorRepo}
                  onChange={(e) => setAnchorRepo(e.target.value)}
                  disabled={reposQ.isLoading || repoOptions.length === 0}
                  data-testid="new-epic-repo"
                  className="block w-full border border-slate-300 rounded-md text-sm px-2.5 py-1.5 bg-white font-mono"
                >
                  {reposQ.isLoading && <option>Loading repos…</option>}
                  {!reposQ.isLoading && repoOptions.length === 0 && <option>No repos attached to any project yet</option>}
                  {repoOptions.map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              )}
              {kind === 'cross-repo' && (
                <p className="text-xs text-slate-500 mt-1">The issue is created here. Operator comments (/proceed, /abort) post on the main repo.</p>
              )}
            </div>

            {kind === 'cross-repo' && (
              <fieldset>
                <legend className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-1.5">
                  Repos in scope
                </legend>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5" data-testid="new-epic-involved">
                  {repoOptions.map((r) => {
                    const checked = involvedRepos.includes(r) || r === anchorRepo;
                    const isAnchor = r === anchorRepo;
                    return (
                      <label key={r} className={'flex items-center gap-2 text-xs ' + (isAnchor ? 'text-slate-500' : 'text-slate-700 cursor-pointer')}>
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={isAnchor}
                          onChange={() => toggleInvolved(r)}
                          className="rounded border-slate-300"
                        />
                        <span className="font-mono truncate">{r}</span>
                        {isAnchor && <StatusBadge tone="neutral">anchor</StatusBadge>}
                      </label>
                    );
                  })}
                </div>
                {involvedError && (
                  <p role="alert" className="mt-2 text-xs text-danger-600">{involvedError}</p>
                )}
              </fieldset>
            )}

            <div>
              <label htmlFor="epic-title" className="block text-xs uppercase tracking-wide text-slate-500 font-semibold mb-1.5">
                Title
              </label>
              <input
                id="epic-title"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Short summary, e.g. 'Add full-text search'"
                maxLength={280}
                data-testid="new-epic-title"
                className="block w-full border border-slate-300 rounded-md text-sm px-2.5 py-1.5"
              />
              {titleError && <p role="alert" className="mt-1 text-xs text-danger-600">{titleError}</p>}
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label htmlFor="epic-body" className="block text-xs uppercase tracking-wide text-slate-500 font-semibold">
                  Describe the outcome
                </label>
                <button
                  type="button"
                  onClick={useTemplate}
                  data-testid="new-epic-use-template"
                  className="text-xs text-primary-500 hover:text-primary-600 hover:underline"
                >
                  Use a template
                </button>
              </div>
              <textarea
                id="epic-body"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={10}
                placeholder="What should be true when this is done?"
                data-testid="new-epic-body"
                className="block w-full border border-slate-300 rounded-md text-sm px-2.5 py-1.5 font-mono"
              />
              {bodyError && <p role="alert" className="mt-1 text-xs text-danger-600">{bodyError}</p>}
            </div>

            {kind !== 'bugfix' && (
              <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={autoApprove}
                  onChange={(e) => setAutoApprove(e.target.checked)}
                  data-testid="new-epic-auto-approve"
                  className="rounded border-slate-300 mt-0.5"
                />
                <span>
                  Start building without plan review
                  <span className="block text-xs text-slate-500">
                    Skip the <code>/proceed</code> gate after planning — orchestrator starts building immediately.
                  </span>
                </span>
              </label>
            )}

            <details data-testid="new-epic-preview" className="text-xs text-slate-600">
              <summary className="cursor-pointer select-none text-slate-500 hover:text-slate-700">
                What gets posted to GitHub
              </summary>
              <p className="mt-1.5 font-mono">
                {describeIssue(kind, anchorRepo, involvedRepos)}
              </p>
            </details>

            {create.error && (
              <div role="alert" className="text-sm text-danger-700 bg-danger-50 border border-danger-100 rounded-md px-3 py-2">
                {(create.error as Error).message}
              </div>
            )}

            <div className="flex items-center gap-2 pt-1">
              <Button type="submit" variant="primary" loading={create.isPending}>
                Start epic
              </Button>
              <Link to="/" className="text-sm text-slate-600 hover:text-slate-900 px-3 py-2">
                Cancel
              </Link>
            </div>
          </form>
        )}
      </main>
    </div>
  );
};

/**
 * Once the issue is created, poll /epics for the new META row to land
 * and then navigate to the detail page. Falls back to a "view on
 * GitHub" link if the wait runs over 60s.
 */
const PostCreateWaiting: React.FC<{
  issueNumber: number;
  issueUrl: string;
  /** Single-repo / bugfix only — when set, we know the exact (repo, n) to match. */
  expectedRepo: string | null;
}> = ({ issueNumber, issueUrl, expectedRepo }) => {
  const navigate = useNavigate();
  const [startedAt] = useState(() => Date.now());
  const [givenUp, setGivenUp] = useState(false);

  const poll = useQuery({
    queryKey: ['epics', 'await-create', issueNumber, expectedRepo],
    queryFn: () => fetchEpics({ limit: 100 }),
    refetchInterval: givenUp ? false : POLL_MS,
    refetchIntervalInBackground: false,
  });

  useEffect(() => {
    if (!poll.data) return;
    const match = poll.data.data.epics.find((e) => {
      if (e.number !== issueNumber) return false;
      if (expectedRepo && e.repo !== expectedRepo) return false;
      return true;
    });
    if (match) {
      navigate(`/epics/${encodeURIComponent(match.epicRef)}`);
    } else if (Date.now() - startedAt > POLL_CAP_MS) {
      setGivenUp(true);
    }
  }, [poll.data, issueNumber, expectedRepo, startedAt, navigate]);

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-card p-5 space-y-3" data-testid="new-epic-waiting">
      <div className="flex items-center gap-2">
        {!givenUp && (
          <span aria-hidden className="inline-block h-3 w-3 rounded-full border-2 border-primary-400 border-t-transparent animate-spin" />
        )}
        <div className="text-sm font-medium text-slate-900">
          Issue #{issueNumber} created
        </div>
      </div>
      <div className="text-sm text-slate-600">
        {givenUp ? (
          <>
            Forge hasn't picked it up yet. You can open the issue directly on GitHub or wait for the dashboard's next refresh.
          </>
        ) : (
          <>
            Waiting for Forge to pick it up — usually a few seconds. The dashboard polls every 2s; we'll jump you to the new epic's detail page the moment it lands.
          </>
        )}
      </div>
      <div className="flex items-center gap-3">
        <a
          href={issueUrl}
          target="_blank"
          rel="noreferrer"
          className="text-sm text-primary-500 hover:text-primary-600 hover:underline"
        >
          View on GitHub ↗
        </a>
        <Link to="/" className="text-sm text-slate-600 hover:text-slate-900">
          Back to epics
        </Link>
      </div>
    </div>
  );
};

export default NewEpicPage;
